import {
  EDatasourceType,
  ESdChangeReason,
  IActionParameter,
  IDatasourceDto,
  IImportMappedExcelColumnMap
} from "@kuika/kuika-cl-model";
import * as _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import BooleanSvg from "../../../../svgs/parameter-type-icons/boolean.svg";
import DateTimeOffsetSvg from "../../../../svgs/parameter-type-icons/date-time-offset.svg";
import DateTimeSvg from "../../../../svgs/parameter-type-icons/date-time.svg";
import DateSvg from "../../../../svgs/parameter-type-icons/date.svg";
import DecimalSvg from "../../../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../../../svgs/parameter-type-icons/double.svg";
import FileContentSvg from "../../../../svgs/parameter-type-icons/file-content.svg";
import GuidSvg from "../../../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../../../svgs/parameter-type-icons/int64.svg";
import LocationSvg from "../../../../svgs/parameter-type-icons/location.svg";
import StringSvg from "../../../../svgs/parameter-type-icons/string.svg";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";

export interface IImportMappedExcelSymbolsGroupProps {
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

export interface IImportMappedExcelSymbolsGroupState {}

class ImportMappedExcelSymbolsGroup extends PureSdComponent<
  IImportMappedExcelSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IImportMappedExcelSymbolsGroupState
> {
  constructor(props: IImportMappedExcelSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  getManagedDBTableName = (): string | undefined => {
    const tableNameParam = this.props.parameters?.find((parameter) => parameter.name === "tableName");
    const value: string | undefined = tableNameParam?.symbol?.fixedValue;

    if (_.isEmpty(value)) {
      return undefined;
    }

    return value;
  };

  getPrimaryKey = (): string | undefined => {
    const primaryKeyParam = this.props.parameters?.find((parameter) => parameter.name === "primaryKey");
    const value: string | undefined = primaryKeyParam?.symbol?.fixedValue;

    if (_.isEmpty(value)) {
      return undefined;
    }

    return value;
  };

  getMappedColumns = (): IImportMappedExcelColumnMap[] => {
    const mappedColumnsParam = this.props.parameters?.find((parameter) => parameter.name === "mappedColumns");
    const value: string | undefined = mappedColumnsParam?.symbol?.fixedValue;

    if (!value || _.isEmpty(value)) {
      return [];
    }

    return JSON.parse(value);
  };

  private getTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return <StringSvg />;
      case "boolean":
        return <BooleanSvg />;
      case "bytea":
        return <DoubleSvg />;
      case "byte":
        return <DoubleSvg />;
      case "date":
        return <DateSvg />;
      case "datetime":
        return <DateTimeSvg />;
      case "location":
        return <LocationSvg />;
      case "filecontent":
        return <FileContentSvg />;
      case "datetimeoffset":
        return <DateTimeOffsetSvg />;
      case "decimal":
        return <DecimalSvg />;
      case "double":
        return <DoubleSvg />;
      case "float":
        return <DoubleSvg />;
      case "varbinary":
        return <DoubleSvg />;
      case "guid":
        return <GuidSvg />;
      case "int16":
        return <Int16Svg />;
      case "int32":
        return <Int32Svg />;
      case "int64":
        return <Int64Svg />;
      case "single":
        return <DoubleSvg />;
      case "string":
        return <StringSvg />;
      case "image":
        return <ImageSvg />;
      case "int":
        return <Int32Svg />;
      case "bigint":
        return <Int64Svg />;
      case "smallint":
        return <Int16Svg />;
      case "nvarchar":
        return <StringSvg />;
      case "uniqueidentifier":
        return <GuidSvg />;
      case "bit":
        return <BooleanSvg />;
      default:
        return <StringSvg />;
    }
  };

  render() {
    const mappedColumns = this.getMappedColumns();
    const managedDbDataSource: IDatasourceDto | undefined = this.props.sd.datasources.find(
      (ds: IDatasourceDto) => ds.datasourceType === EDatasourceType.ManagedDb
    );
    const schema = this.props.sd.schemas.get(managedDbDataSource?.id ?? "");
    const table = schema?.tables.find((table) => table.name === this.getManagedDBTableName());
    const primaryKeyColumn = table?.columns.find((column) => column.name === this.getPrimaryKey());

    return (
      <div className="kuika_property-grid-column">
        <div>
          <div className="kuika_add_actions_properties_name">ManagedDB Table</div>
          <div className="kuika_add_actions_properties_readonly">
            {this.getManagedDBTableName() ?? "No table selected"}
          </div>
        </div>
        <div>
          <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
            Primary Key
          </div>
          <div
            className="kuika_add_actions_properties_readonly"
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {primaryKeyColumn && this.getTypeIcon(primaryKeyColumn.kuikaDataType)}
            {this.getPrimaryKey() ?? "No primary key selected"}
          </div>
        </div>
        {mappedColumns.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div className="kuika_add_actions_properties_name">Mapped Columns</div>
            {mappedColumns
              .filter((mappedColumn: IImportMappedExcelColumnMap) => mappedColumn.isEnabled)
              .map((mappedColumn: IImportMappedExcelColumnMap) => {
                const column = table?.columns.find((c) => c.name === mappedColumn.source);
                return (
                  <div
                    className="kuika_add_actions_properties_readonly"
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {column && this.getTypeIcon(column?.kuikaDataType)}
                    {mappedColumn.alias}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    );
  };
}

const importMappedExcelSymbolsGroup = withContext<IImportMappedExcelSymbolsGroupProps>(
  withSD<IImportMappedExcelSymbolsGroupProps>(ImportMappedExcelSymbolsGroup)
);
export { importMappedExcelSymbolsGroup as ImportMappedExcelSymbolsGroup };
